import { z } from 'zod';

const integerSchema = z
  .union([
    z.number(),
    z
      .string()
      .trim()
      .regex(/^[+-]? *\d+$/)
      .transform((value) => Number(value.replaceAll(' ', ''))),
  ])
  .pipe(z.number().int().safe());

const floatingSchema = z
  .union([
    z.number(),
    z
      .string()
      .trim()
      .regex(/^[+-]? *\d+(?:[.,]\d+)?$/)
      .transform((value) => Number(value.replaceAll(' ', '').replace(',', '.'))),
  ])
  .pipe(z.number().finite());

const floatingParsingSchema = z
  .union([
    z.number(),
    z
      .string()
      .trim()
      .regex(/^[+-]? *(?:\d+(?:[.,]\d*)?|[.,]\d+)$/)
      .transform((value) => Number(value.replaceAll(' ', '').replace(',', '.'))),
  ])
  .pipe(z.number().finite());

/**
 * Строго преобразует значение в безопасное целое число.
 *
 * Строка может содержать знак, пробелы вокруг числа и между знаком и цифрами,
 * но не может содержать дробную часть или посторонние символы.
 *
 * @param value Преобразуемое значение.
 * @returns Безопасное целое число или `null`, если преобразование невозможно.
 */
export function toInteger(value: unknown): number | null {
  const parsingResult = integerSchema.safeParse(value);

  return parsingResult.success ? parsingResult.data : null;
}

/**
 * Строго преобразует значение в конечное число с плавающей точкой.
 *
 * Строка может содержать знак, пробелы вокруг числа и между знаком и цифрами,
 * а также точку или запятую в качестве дробного разделителя. Посторонние
 * символы не допускаются.
 *
 * @param value Преобразуемое значение.
 * @returns Конечное число или `null`, если преобразование невозможно.
 */
export function toFloating(value: unknown): number | null {
  const parsingResult = floatingSchema.safeParse(value);

  return parsingResult.success ? parsingResult.data : null;
}

/**
 * Толерантно преобразует значение в безопасное целое число.
 *
 * Поддерживает пробелы, знак, точку или запятую в качестве дробного
 * разделителя, а также неполные дроби вроде `.5` и `5,`. Дробная часть
 * отбрасывается с помощью `Math.trunc`, а посторонние символы не допускаются.
 *
 * @param value Разбираемое значение.
 * @returns Безопасное целое число или `null`, если преобразование невозможно.
 * @example parseInteger(' - 23.56 ') // -23
 */
export function parseInteger(value: unknown): number | null {
  const parsingResult = floatingParsingSchema.safeParse(value);

  return parsingResult.success ? toInteger(Math.trunc(parsingResult.data)) : null;
}

/**
 * Извлекает первое целое число из произвольного значения.
 *
 * Числовые значения усекаются с помощью `Math.trunc`. Для строк сохраняется
 * относящийся к числу знак, а остальные символы до и после числа игнорируются.
 *
 * @param value Разбираемое значение.
 * @returns Безопасное целое число или `null`, если число не найдено.
 * @example extractInteger(' + 1 foo 2') // 1
 * @example extractInteger('-23.56fo') // -23
 */
export function extractInteger(value: unknown): number | null {
  if (typeof value === 'number') {
    return toInteger(Math.trunc(value));
  }

  if (typeof value !== 'string') {
    return null;
  }

  const match = /[+-]? *\d+/.exec(value);

  return match ? toInteger(match[0].replaceAll(' ', '')) : null;
}

/**
 * Толерантно преобразует значение в конечное число с плавающей точкой.
 *
 * Поддерживает пробелы, знак, точку или запятую в качестве дробного
 * разделителя, а также неполную запись дроби вроде `.5` или `1,`.
 * Посторонние символы не допускаются.
 *
 * @param value Разбираемое значение.
 * @returns Конечное число или `null`, если преобразование невозможно.
 * @example parseFloating(' - 23,56 ') // -23.56
 */
export function parseFloating(value: unknown): number | null {
  const parsingResult = floatingParsingSchema.safeParse(value);

  return parsingResult.success ? parsingResult.data : null;
}
